<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { debounce } from '@chatwoot/utils';
import { useStore } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import FeatureSpotlightPopover from 'dashboard/components-next/feature-spotlight/FeatureSpotlightPopover.vue';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import { fetchWooCommerceAllOrders } from '../../api/woocommerce';
import WooCommerceBrandIcon from '../../components/WooCommerceBrandIcon.vue';
import WooCommerceStoreSwitcher from '../../components/WooCommerceStoreSwitcher.vue';
import {
  buildConversationRoute,
  buildWooCommerceContactRoute,
  buildWooCommerceOrderDetailsRoute,
  getLatestConversation,
} from '../../helpers/navigation';
import {
  buildWooCommerceStoreOptions,
  isWooCommerceHookSelectionValid,
} from '../../helpers/stores';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const isLoading = ref(true);
const records = ref([]);
const error = ref('');
const searchQuery = ref('');
const openActionMenuId = ref(null);
const pagination = ref({
  totalCount: 0,
  currentPage: 1,
  totalPages: 0,
  itemsPerPage: 25,
});

const accountId = computed(() => Number(route.params.accountId));
const selectedHookId = computed(() => route.params.hookId?.toString() || '');
const currentPage = computed(() => {
  const page = Number(route.query.page || 1);
  return Number.isFinite(page) && page > 0 ? page : 1;
});
const integration = computed(() => {
  const getter = store.getters['integrations/getIntegration'];
  return getter ? getter('woocommerce', {}) : { hooks: [] };
});
const availableStores = computed(() =>
  buildWooCommerceStoreOptions(integration.value?.hooks || [])
);
const selectedStoreLabel = computed(
  () =>
    route.query.store_name?.toString() ||
    availableStores.value.find(
      storeOption => storeOption.id === selectedHookId.value
    )?.label ||
    ''
);
const selectedHooks = computed(() => {
  const hooks = integration.value?.hooks || [];

  return selectedHookId.value
    ? hooks.filter(hook => String(hook.id) === selectedHookId.value)
    : hooks;
});
const translatedError = computed(() =>
  error.value ? t('WOOCOMMERCE_PAGES.ORDERS.ERROR') : ''
);
const showPaginationFooter = computed(() => pagination.value.totalPages > 1);
const isEmptyState = computed(() => !records.value.length && !error.value);
const pendingBackfillMessage = computed(() => {
  const hasPendingState = selectedHooks.value.some(hook => {
    const status =
      hook.settings?.orders_backfill?.status || hook.orders_backfill?.status;
    const cachedOrdersCount =
      hook.settings?.cached_orders_count || hook.cached_orders_count || 0;

    return ['queued', 'running'].includes(status) && cachedOrdersCount === 0;
  });

  return hasPendingState ? t('WOOCOMMERCE_PAGES.ORDERS.EMPTY_PENDING') : '';
});

const customerDetails = order =>
  [order.customer_email || '—', order.customer_phone || '—'].join(' · ');

const orderDetails = order =>
  [
    order.status || '—',
    order.payment_status || '—',
    [order.total || '—', order.currency || ''].filter(Boolean).join(' '),
  ].join(' · ');

const primaryCustomer = order =>
  order.customer_name || order.customer_email || order.customer_phone || '—';

const orderActionMenuId = order => order.record_id || order.id || order.number;

const toggleActionMenu = order => {
  const menuId = orderActionMenuId(order);
  openActionMenuId.value = openActionMenuId.value === menuId ? null : menuId;
};

const closeActionMenu = () => {
  openActionMenuId.value = null;
};

const runOrderAction = action => {
  closeActionMenu();
  action();
};

const loadRecords = async () => {
  try {
    if (
      !isWooCommerceHookSelectionValid(
        integration.value?.hooks || [],
        selectedHookId.value
      )
    ) {
      await router.replace({
        name: route.name,
        params: {
          ...route.params,
          hookId: undefined,
        },
        query: {
          ...route.query,
          page: undefined,
          store_name: undefined,
        },
      });
      return;
    }

    isLoading.value = true;
    error.value = '';

    const { data } = await fetchWooCommerceAllOrders(accountId.value, {
      page: currentPage.value,
      per_page: pagination.value.itemsPerPage,
      search: searchQuery.value.trim() || undefined,
      hook_id: selectedHookId.value || undefined,
    });

    records.value = data.orders || [];
    pagination.value = {
      totalCount: data.meta?.total_count || records.value.length,
      currentPage: data.meta?.current_page || currentPage.value,
      totalPages: data.meta?.total_pages || 0,
      itemsPerPage: data.meta?.items_per_page || pagination.value.itemsPerPage,
    };
  } catch (err) {
    if (err?.response?.status === 404 && selectedHookId.value) {
      await store.dispatch('integrations/get', 'woocommerce').catch(() => {});
      await router.replace({
        name: route.name,
        params: {
          ...route.params,
          hookId: undefined,
        },
        query: {
          ...route.query,
          page: undefined,
          store_name: undefined,
        },
      });
      return;
    }

    error.value = 'WOOCOMMERCE_PAGES.ORDERS.ERROR';
  } finally {
    isLoading.value = false;
  }
};

const updateURLWithFilters = page => {
  const nextQuery = {
    ...route.query,
    page: page > 1 ? String(page) : undefined,
    search: searchQuery.value.trim() || undefined,
  };

  const currentSearch = route.query.search?.toString() || '';
  const nextSearch = nextQuery.search || '';

  if (currentPage.value === page && currentSearch === nextSearch) {
    loadRecords();
    return;
  }

  router.replace({
    name: route.name,
    params: route.params,
    query: nextQuery,
  });
};

const updatePage = page => {
  updateURLWithFilters(page);
};

const debouncedSearch = debounce(() => {
  updateURLWithFilters(1);
}, 500);

const openOrder = order => {
  if (!order?.record_id) return;

  router.push(
    buildWooCommerceOrderDetailsRoute(accountId.value, order.record_id, {
      hook_id: selectedHookId.value || undefined,
      page: currentPage.value > 1 ? String(currentPage.value) : undefined,
      search: searchQuery.value.trim() || undefined,
      store_name: selectedStoreLabel.value || undefined,
    })
  );
};

const openExternalOrder = orderUrl => {
  if (!orderUrl) return;

  window.open(orderUrl, '_blank', 'noopener,noreferrer');
};

const openContact = contactId => {
  if (!contactId) return;

  router.push(buildWooCommerceContactRoute(accountId.value, contactId));
};

const openLatestConversation = async contactId => {
  if (!contactId) return;

  await store.dispatch('contactConversations/get', contactId);
  const conversationsGetter =
    store.getters['contactConversations/getContactConversation'];
  const conversations = conversationsGetter
    ? conversationsGetter(contactId)
    : [];
  const latestConversation = getLatestConversation(conversations);

  if (!latestConversation?.id) {
    openContact(contactId);
    return;
  }

  router.push(buildConversationRoute(accountId.value, latestConversation.id));
};

const syncFromRoute = () => {
  closeActionMenu();
  searchQuery.value = route.query.search?.toString() || '';
  loadRecords();
};

onMounted(async () => {
  await store.dispatch('integrations/get', 'woocommerce').catch(() => {});
  syncFromRoute();
});

watch(
  () => [route.params.hookId, route.query.page, route.query.search].join('|'),
  () => {
    syncFromRoute();
  }
);
</script>

<template>
  <SettingsLayout :is-loading="isLoading" class="gap-6">
    <template #header>
      <div
        data-target="woocommerce-orders-page"
        class="w-full max-w-[72rem] mx-auto px-4 sm:px-6 pt-4 sm:pt-6"
      >
        <div class="flex flex-col gap-4 rounded-md border border-n-weak bg-n-alpha-2 p-4 sm:p-5">
          <div class="flex min-w-0 gap-3">
            <WooCommerceBrandIcon :size="28" class="shrink-0" />
            <div class="min-w-0">
              <h1 class="mb-1 text-2xl sm:text-3xl font-semibold text-n-slate-12">
                {{ $t('WOOCOMMERCE_PAGES.ORDERS.HEADER_SHORT') }}
              </h1>
              <p class="mb-0 max-w-4xl text-sm sm:text-base text-n-slate-11">
                {{
                  selectedStoreLabel
                    ? $t('WOOCOMMERCE_PAGES.ORDERS.DESCRIPTION_FOR_STORE', {
                        store: selectedStoreLabel,
                      })
                    : $t('WOOCOMMERCE_PAGES.ORDERS.DESCRIPTION')
                }}
              </p>
            </div>
          </div>

          <div
            class="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between"
          >
            <div class="flex flex-wrap items-center gap-3">
              <div
                v-if="availableStores.length"
                data-target="woocommerce-orders-store-switcher"
              >
                <WooCommerceStoreSwitcher
                  :stores="availableStores"
                  :selected-hook-id="selectedHookId"
                  show-all-stores
                />
              </div>
              <FeatureSpotlightPopover
                :button-label="
                  $t('WOOCOMMERCE_PAGES.ORDERS.FEATURE_SPOTLIGHT.BUTTON')
                "
                :title="$t('WOOCOMMERCE_PAGES.ORDERS.FEATURE_SPOTLIGHT.TITLE')"
                :note="$t('WOOCOMMERCE_PAGES.ORDERS.FEATURE_SPOTLIGHT.NOTE')"
                fallback-thumbnail="/assets/images/dashboard/captain/faqs-popover-light.svg"
                fallback-thumbnail-dark="/assets/images/dashboard/captain/faqs-popover-dark.svg"
                learn-more-url="https://woocommerce.com/document/managing-orders/"
              />
            </div>

            <Input
              v-model="searchQuery"
              data-target="woocommerce-orders-search"
              :placeholder="$t('WOOCOMMERCE_PAGES.ORDERS.SEARCH_PLACEHOLDER')"
              class="w-full xl:w-96"
              size="sm"
              type="search"
              autofocus
              @input="debouncedSearch"
            />
          </div>
        </div>
      </div>
    </template>

    <template #body>
      <div class="w-full max-w-[72rem] mx-auto px-4 sm:px-6 pb-6 flex flex-col gap-4 sm:gap-6">
        <div
          v-if="error"
          class="rounded-md border border-n-ruby-5 bg-n-ruby-2 p-4 text-sm text-n-ruby-12"
        >
          {{ translatedError }}
        </div>
        <div
          v-else-if="isEmptyState"
          class="rounded-md border border-dashed border-n-weak bg-n-alpha-2 p-5 sm:p-6 text-sm text-n-slate-11"
        >
          {{ pendingBackfillMessage || $t('WOOCOMMERCE_PAGES.ORDERS.EMPTY') }}
        </div>
        <div v-else class="grid gap-4">
          <div
            v-for="order in records"
            :key="order.id"
            class="rounded-md border border-n-weak bg-n-alpha-2 p-4 shadow-sm"
          >
            <div
              class="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between"
            >
              <div class="min-w-0 space-y-2">
                <p class="mb-0 text-lg font-semibold text-n-slate-12">
                  {{
                    $t('WOOCOMMERCE_PAGES.ORDERS.ORDER_NUMBER', {
                      number: order.number || order.id,
                    })
                  }}
                </p>
                <p class="mb-0 text-sm font-medium text-n-slate-11">
                  {{ primaryCustomer(order) }}
                </p>
                <div class="flex flex-wrap gap-2">
                  <span class="rounded-md bg-n-alpha-3 px-2 py-1 text-xs font-medium text-n-slate-11">
                    {{ customerDetails(order) }}
                  </span>
                  <span class="rounded-md bg-n-alpha-3 px-2 py-1 text-xs font-medium text-n-slate-11">
                    {{ orderDetails(order) }}
                  </span>
                  <span
                    v-if="order.source_label"
                    class="rounded-md bg-n-alpha-3 px-2 py-1 text-xs font-medium text-n-slate-11"
                  >
                    {{ order.source_label }}
                  </span>
                </div>
              </div>
              <div class="relative flex w-full items-center gap-2 sm:w-auto xl:justify-end">
                <Button
                  icon="i-lucide-receipt-text"
                  size="sm"
                  class="min-w-0 flex-1 justify-center text-sm sm:flex-none"
                  :disabled="!order.record_id"
                  :label="$t('WOOCOMMERCE_PAGES.OPEN_ORDER')"
                  @click="openOrder(order)"
                />
                <OnClickOutside @trigger="closeActionMenu">
                  <div class="relative">
                    <button
                      type="button"
                      class="flex size-9 items-center justify-center rounded-md border border-n-weak bg-n-alpha-2 text-n-slate-11 transition hover:bg-n-alpha-3 hover:text-n-slate-12"
                      :aria-label="$t('WOOCOMMERCE_PAGES.MORE_ACTIONS')"
                      :title="$t('WOOCOMMERCE_PAGES.MORE_ACTIONS')"
                      @click="toggleActionMenu(order)"
                    >
                      <span class="i-lucide-ellipsis-vertical size-4" />
                    </button>
                    <div
                      v-if="openActionMenuId === orderActionMenuId(order)"
                      class="absolute right-0 top-11 z-20 w-64 overflow-hidden rounded-md border border-n-weak bg-n-solid-2 p-1 shadow-lg"
                    >
                      <button
                        v-if="order.admin_url"
                        type="button"
                        class="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
                        @click="
                          runOrderAction(() => openExternalOrder(order.admin_url))
                        "
                      >
                        <span class="i-lucide-external-link size-4 text-n-slate-10" />
                        <span>{{ $t('WOOCOMMERCE_PAGES.OPEN_IN_WOOCOMMERCE') }}</span>
                      </button>
                      <button
                        type="button"
                        class="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
                        :disabled="!order.contact_id"
                        @click="runOrderAction(() => openContact(order.contact_id))"
                      >
                        <span class="i-lucide-user-round size-4 text-n-slate-10" />
                        <span>{{ $t('WOOCOMMERCE_PAGES.OPEN_CONTACT_TO_MESSAGE') }}</span>
                      </button>
                      <button
                        type="button"
                        class="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2 disabled:cursor-not-allowed disabled:opacity-50"
                        :disabled="!order.contact_id"
                        @click="
                          runOrderAction(() =>
                            openLatestConversation(order.contact_id)
                          )
                        "
                      >
                        <span class="i-lucide-message-circle size-4 text-n-slate-10" />
                        <span>{{ $t('WOOCOMMERCE_PAGES.OPEN_LAST_CONVERSATION') }}</span>
                      </button>
                    </div>
                  </div>
                </OnClickOutside>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <PaginationFooter
      v-if="showPaginationFooter && !error"
      :current-page="pagination.currentPage"
      :total-items="pagination.totalCount"
      :items-per-page="pagination.itemsPerPage"
      @update:current-page="updatePage"
    />
  </SettingsLayout>
</template>
